import React, { FC, useEffect, useRef } from 'react';

interface SexAtBirthPopoverProps {
  isFr: boolean;
  onClose: () => void;
  onLearnMore: () => void;
  onIllAnswer: () => void;
  onSkipAnyway: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const SexAtBirthPopover: FC<SexAtBirthPopoverProps> = ({ isFr, onClose, onLearnMore, onIllAnswer, onSkipAnyway, onMouseEnter, onMouseLeave }) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={popoverRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="absolute z-50 top-full left-0 mt-2 w-full max-w-[400px] bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-brand-border dark:border-slate-700 p-4"
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-sm font-bold text-brand-text dark:text-white">
          {isFr ? 'Pourquoi posons-nous cette question?' : 'Why are we asking this?'}
        </h4>
        <button
          onClick={onClose}
          className="p-0.5 text-brand-text-muted hover:text-brand-text dark:hover:text-white transition-colors flex-shrink-0 ml-2"
          aria-label="Close"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>close</span>
        </button>
      </div>
      <p className="text-sm text-brand-text-light dark:text-slate-300 leading-relaxed">
        {isFr
          ? "Le sexe à la naissance influence la façon dont les maladies se développent, se manifestent et répondent aux traitements tout au long de la vie. Chez les personnes âgées, il détermine les risques cardiovasculaires, la densité osseuse, la fonction immunitaire, le métabolisme des médicaments et la présentation des symptômes. Cette donnée permet aux médecins, infirmier(ère)s et ergothérapeutes d'adapter les soins de votre proche — et d'éviter une approche uniforme."
          : "Sex assigned at birth affects how diseases develop, present, and respond to treatment throughout life. In older adults, it influences cardiovascular risk, bone density, immune function, medication metabolism, and symptom presentation — meaning the same condition can look and behave very differently in men and women. This information helps physicians, nurses, and occupational therapists provide targeted, safer care for your loved one — not a one-size-fits-all approach."
        }
      </p>
      <div className="mt-3 flex gap-2">
        <button
          onClick={onIllAnswer}
          className="flex-1 px-3 py-2 rounded-lg bg-brand-primary text-white text-sm font-semibold hover:bg-brand-primary/90 transition-colors"
        >
          {isFr ? 'Je vais répondre' : "I'll answer"}
        </button>
        <button
          onClick={onSkipAnyway}
          className="flex-1 px-3 py-2 rounded-lg border-2 border-brand-border dark:border-slate-600 text-brand-text-light dark:text-slate-300 text-sm font-semibold hover:border-brand-primary/50 transition-colors"
        >
          {isFr ? 'Passer quand même' : 'Skip anyway'}
        </button>
      </div>
      <button
        onClick={onLearnMore}
        className="mt-2 text-xs text-brand-text-muted hover:text-brand-primary hover:underline bg-transparent border-none cursor-pointer p-0 transition-colors"
      >
        {isFr
          ? 'En savoir plus sur le sexe et la santé gériatrique →'
          : 'Learn more about sex & geriatric health →'
        }
      </button>
    </div>
  );
};

export default SexAtBirthPopover;
